import { faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { fetchChallengingWords } from '../../API/ArticleApi';
import Button from '../../Components/Button';
import { useSession } from '../../Context/ReadingSessionProvider';
import Modal from '../../Modal/Modal';
import { DictionaryLookupEvent } from '../../Session/SessionEvents/DictionaryLookupEvent';
import { Article } from '../../Types/Article';
import { WORD_REGEX_EXPRESSION } from '../../Utils/Text';
import { useArticleReadAloud } from '../hooks/useArticleReadAloud';
import { Paragraph, Section } from '../Types/ArticleTypes';
import ArticleReadAloud from './ArticleReadAloud';
import Dictionary from './Dictionary';
import ParagraphReadAloud from './ParagraphReadAloud';
import ShareArticle from './ShareArticle';

interface ArticleContentProps {
  article: Article;
  sections: Section[];
  paragraphs: Paragraph[];
  newlyRevealedSection: number | null;
}

export default function ArticleContent({
  article,
  sections,
  paragraphs,
  newlyRevealedSection,
}: ArticleContentProps) {
  const { recordEvent } = useSession();
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedWordIndex, setSelectedWordIndex] = useState<{
    lineIndex: number;
    partIndex: number;
  } | null>(null);
  const [showDefinitionOverlay, setShowDefinitionOverlay] = useState(false);
  const [challengingWords, setChallengingWords] = useState<string[]>();
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    playbackState,
    playbackParagraph,
    playbackMarker,
    isReadyForPlayback,
    audioManager,
    playReadAloud,
    pauseReadAloud,
    stopReadAloud,
    changePlaybackMarker,
    changePlaybackParagraph,
    setReadyForPlayback,
  } = useArticleReadAloud(sections, paragraphs);

  const playbackParagraphRef = useRef(playbackParagraph);
  playbackParagraphRef.current = playbackParagraph;

  useEffect(() => {
    if (!article) return;
    if (challengingWords) return;

    const fetchAndSetChallengingWords = async () => {
      try {
        const fetchedChallengingWords = await fetchChallengingWords(article.id);

        if (fetchedChallengingWords?.length) {
          setChallengingWords(fetchedChallengingWords);
        }
      } catch (error) {
        console.error('Error fetching challenging words:', error);
      }
    };

    fetchAndSetChallengingWords();
  }, [article, challengingWords]);

  const handleWordClick = async (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    word: string,
    paragraph: string,
    lineIndex: number,
    partIndex: number,
  ) => {
    if (
      selectedWord === word &&
      selectedWordIndex?.lineIndex === lineIndex &&
      selectedWordIndex?.partIndex === partIndex
    ) {
      return;
    }
    setSelectedWord(word);
    setShowDefinitionOverlay(true);
    setSelectedWordIndex({ lineIndex, partIndex });

    const dictionaryLookupEvent = new DictionaryLookupEvent(article.id, word);
    recordEvent(dictionaryLookupEvent);
  };

  const isChallengingWord = (word: string) => {
    return challengingWords?.some(
      (challengingWord) => challengingWord.toLowerCase() === word.toLowerCase(),
    );
  };

  const shouldSectionBeBlurred = (index: number): boolean => {
    if (index === 0) {
      return false;
    }
    return sections[index].isBlurred;
  };

  return (
    <>
      <div className="mt-2 md:mt-0 flex justify-start gap-2">
        <ArticleReadAloud
          playbackState={playbackState}
          playbackParagraph={playbackParagraph}
          articleId={article.id}
          onPlay={() => playReadAloud(playbackParagraph)}
          onPause={pauseReadAloud}
          onStop={stopReadAloud}
        />
        <Button variant="primary" onClick={() => setShowShareModal(true)}>
          <FontAwesomeIcon
            icon={faShare}
            className="h-5 w-5 mr-1 align-middle"
          />
          Share
        </Button>
      </div>
      <div
        className="text-neutral-800 text-opacity-80 text-xl mt-6 mx-auto text-left flex-grow-0 flex-shrink-0
        max-md:max-w-full"
        aria-label="Article Content"
        data-testid="article-content"
      >
        {sections.map((section, sectionIndex) => {
          const isPreviousBlurred = sectionIndex
            ? shouldSectionBeBlurred(sectionIndex - 1)
            : false;
          const isBlurred = shouldSectionBeBlurred(sectionIndex);
          const isHighlighted = newlyRevealedSection === sectionIndex;

          return (
            <React.Fragment key={section.text}>
              {isBlurred && !isPreviousBlurred ? (
                <section
                  id="blur-zone-marker"
                  className="flex gap-2 items-center text-sm text-center text-white mb:4
                  max-sm:hidden"
                >
                  <div className="flex-1 shrink-0 self-stretch my-auto w-full border border-blue-600 border-solid stroke-[1px] stroke-blue-600"></div>
                  <div className="justify-center self-stretch p-1.5 bg-blue-600 rounded-lg max-sm:cursor-pointer max-sm:max-w-[150px]">
                    <span className="sm:hidden">
                      Tap to answer the next guiding question and continue
                      reading
                    </span>
                    <span className="max-sm:hidden">
                      Answer the next guiding question and continue reading
                    </span>
                  </div>
                  <div className="flex-1 shrink-0 self-stretch my-auto w-full border border-blue-600 border-solid stroke-[1px] stroke-blue-600"></div>
                </section>
              ) : null}

              {paragraphs.map((paragraph, paragraphIndex) =>
                // We do it inside the map, and not in a filter, so that paragraphIndex maps to a correct position in the array
                paragraph.startIndex >= section.startIndex &&
                paragraph.endIndex <= section.endIndex ? (
                  <div className="flex flex-row" key={paragraphIndex}>
                    {playbackParagraph && !isBlurred && (
                      <ParagraphReadAloud
                        paragraph={paragraph}
                        playbackParagraph={playbackParagraph}
                        paragraphs={paragraphs}
                        playbackState={playbackState}
                        isAudioLoaded={isReadyForPlayback}
                        audioManager={audioManager}
                        articleId={article.id}
                        onPlayClick={(p) => {
                          if (p === playbackParagraph) {
                            playReadAloud(p);
                          } else {
                            pauseReadAloud();
                            changePlaybackParagraph(p);
                            changePlaybackMarker(null);
                            playReadAloud(p);
                          }
                        }}
                        onPauseClick={pauseReadAloud}
                        onReadyForPlayback={setReadyForPlayback}
                        onPlaybackMarkerChange={changePlaybackMarker}
                        onPlaybackParagraphChange={changePlaybackParagraph}
                        onStop={stopReadAloud}
                      />
                    )}
                    <p
                      key={paragraphIndex}
                      style={{ scrollMarginTop: '20px' }}
                      data-question-id={section.questionId || 'null'}
                      data-testid={`article-paragraph-start-${paragraph.startIndex}`}
                      className={`mb-4 
                        ${
                          isBlurred
                            ? 'blur max-sm:hidden'
                            : playbackMarker?.startIndex ===
                                paragraph.startIndex &&
                              playbackMarker?.endIndex === paragraph.endIndex &&
                              'bg-amber-100'
                        }
                        ${isHighlighted ? 'animate-fadeHighlight' : ''}
                      `}
                    >
                      {paragraph.words.map(
                        ({ word, wordIndex, wordStartsAt }) => (
                          <span
                            key={wordIndex}
                            onClick={(event) => {
                              const regex = new RegExp(WORD_REGEX_EXPRESSION);

                              if (regex.test(word)) {
                                handleWordClick(
                                  event,
                                  word,
                                  paragraph.text,
                                  paragraphIndex,
                                  wordIndex,
                                );
                              }
                            }}
                            className={`relative cursor-pointer 
                              ${
                                selectedWord === word &&
                                selectedWordIndex?.lineIndex ===
                                  paragraphIndex &&
                                selectedWordIndex?.partIndex === wordIndex
                                  ? 'bg-blue-200'
                                  : wordStartsAt + paragraph.startIndex ===
                                      playbackMarker?.startIndex
                                    ? 'bg-amber-100'
                                    : isChallengingWord(word)
                                      ? 'underline decoration-dashed underline-offset-4 decoration-gray-700/70'
                                      : ''
                              }`}
                          >
                            {word}
                            {selectedWord === word &&
                              selectedWordIndex?.lineIndex === paragraphIndex &&
                              selectedWordIndex?.partIndex === wordIndex && (
                                <>
                                  {showDefinitionOverlay && (
                                    <Dictionary
                                      word={word}
                                      context={paragraph.text}
                                      articleLexileLevel={article.lexileLevel}
                                      onClose={() => {
                                        setSelectedWord(null);
                                        setSelectedWordIndex(null);
                                        setShowDefinitionOverlay(false);
                                      }}
                                      onError={(error) => {
                                        setSelectedWord(null);
                                        setSelectedWordIndex(null);
                                        setShowDefinitionOverlay(false);
                                      }}
                                      onCancel={() => {
                                        // Do nothing, we cancel fetching one definition when requesting a new one. So a new overlay is already opened.
                                      }}
                                    />
                                  )}
                                </>
                              )}
                          </span>
                        ),
                      )}
                    </p>
                  </div>
                ) : null,
              )}
            </React.Fragment>
          );
        })}
      </div>

      {article && (
        <Modal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          showCloseButton={true}
        >
          <ShareArticle article={article} />
        </Modal>
      )}
    </>
  );
}
